/**
 * Typed fetch wrapper of the API calls.
 *
 * - Sends the stored token as-is in `Authorization` (the value already carries
 *   the `JWT ` scheme the API expects).
 * - 401  -> clears the session and redirects to /login with the API error code.
 * - 429  -> user-facing toast.
 * - 5xx  -> console.error.
 * - Any non-2xx -> throws ApiError so TanStack Query surfaces it.
 *
 * URLs are relative: production serves API and client from the same origin,
 * and the Vite dev server proxies /api and /auth to :3000.
 */
import type { ZodType } from 'zod';
import { toast } from 'sonner';
import { getToken, logout } from '@/features/auth/session';

export const API_BASE = '/api';
export const AUTH_BASE = '/auth';

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly code: string | undefined;

  constructor(status: number, body: unknown, message?: string) {
    super(message ?? `API request failed with status ${status}`);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
    this.code =
      body &&
      typeof body === 'object' &&
      typeof (body as { code?: unknown }).code === 'string'
        ? (body as { code: string }).code
        : undefined;
  }
}

export type QueryParams = Record<
  string,
  string | number | boolean | null | undefined
>;

export interface RequestOptions<T> {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: QueryParams;
  /** Validates the response at the boundary. Throws ApiError(0) on mismatch. */
  schema?: ZodType<T>;
  signal?: AbortSignal;
}

export const buildUrl = (path: string, query?: QueryParams): string => {
  const url = path.startsWith('/') ? path : `${API_BASE}/${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  Object.entries(query).forEach(([key, value]) => {
    if (value !== undefined && value !== null)
      params.append(key, String(value));
  });
  const qs = params.toString();
  if (!qs) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${qs}`;
};

const parseBody = async (response: Response): Promise<unknown> => {
  const text = await response.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
};

export const handleErrorStatus = (status: number, body: unknown): void => {
  if (status === 401) {
    const code =
      body && typeof body === 'object'
        ? (body as { code?: string }).code
        : undefined;
    logout(code);
  } else if (status === 429) {
    toast.error('Too many requests, please try again later.');
  } else if (status >= 500) {
    console.error(`API error ${status}`, body);
  }
};

export async function request<T = unknown>(
  path: string,
  options: RequestOptions<T> = {},
): Promise<T> {
  const { method = 'GET', body, query, schema, signal } = options;
  const headers = new Headers({ Accept: 'application/json' });
  const token = getToken();
  if (token) headers.set('Authorization', token);
  if (body !== undefined) headers.set('Content-Type', 'application/json');

  const init: RequestInit = { method, headers, credentials: 'same-origin' };
  if (body !== undefined) init.body = JSON.stringify(body);
  if (signal) init.signal = signal;

  const response = await fetch(buildUrl(path, query), init);
  const data = await parseBody(response);

  if (!response.ok) {
    handleErrorStatus(response.status, data);
    throw new ApiError(response.status, data);
  }

  if (schema) {
    const parsed = schema.safeParse(data);
    if (!parsed.success) {
      console.error(
        `Unexpected response shape for ${path}`,
        parsed.error.issues,
      );
      throw new ApiError(0, data, `Unexpected response shape for ${path}`);
    }
    return parsed.data;
  }
  return data as T;
}

/** Authenticated GET of a binary resource (a PDF, a CSV export). */
export const fetchBlob = async (
  path: string,
  accept = 'application/octet-stream',
): Promise<Blob> => {
  const headers = new Headers({ Accept: accept });
  const token = getToken();
  if (token) headers.set('Authorization', token);
  const response = await fetch(buildUrl(path), {
    headers,
    credentials: 'same-origin',
  });
  if (!response.ok) {
    handleErrorStatus(response.status, null);
    throw new ApiError(response.status, null);
  }
  return response.blob();
};

export const api = {
  get: <T>(
    path: string,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'GET' }),
  post: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'POST', body }),
  put: <T>(
    path: string,
    body?: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'PUT', body }),
  patch: <T>(
    path: string,
    body: unknown,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(
    path: string,
    options?: Omit<RequestOptions<T>, 'method' | 'body'>,
  ) => request<T>(path, { ...options, method: 'DELETE' }),
};
